"use client";

import { c as _c } from "react/compiler-runtime";
import React, { forwardRef, useRef } from "react";
import { cx } from "@/lib/tailwind-utils";
interface ChatComposerFrameProps {
  size?: "sm" | "lg";
  /** Raises the frame so a non-default send channel is unmistakable. */
  emphasized?: boolean;
  leading?: React.ReactNode;
  /** The editor itself — Tiptap in the inbox, a textarea in the portal. */
  children: React.ReactNode;
  trailing?: React.ReactNode;
  className?: string;
}

/** The rounded composer shell shared by the staff inbox and patient portal. */
export const ChatComposerFrame = forwardRef<HTMLDivElement, ChatComposerFrameProps>(function ChatComposerFrame(t0, ref) {
  const $ = _c(10);
  const {
    size: t1,
    emphasized,
    leading,
    children,
    trailing,
    className
  } = t0;
  const size = t1 === undefined ? "lg" : t1;
  const t2 = emphasized ? "border-primary shadow-lg shadow-primary/30 ring-2 ring-primary/25 focus-within:ring-primary/50" : "border-border shadow-md focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20";
  const t3 = size === "sm" ? "rounded-2xl" : "rounded-3xl";
  let t4;
  if ($[0] !== className || $[1] !== t2 || $[2] !== t3) {
    t4 = cx("relative flex min-w-full items-center gap-2 border px-2 py-2", "bg-background transition-all duration-300", t2, t3, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== children || $[5] !== leading || $[6] !== ref || $[7] !== t4 || $[8] !== trailing) {
    t5 = <div ref={ref} className={t4}>{leading}{children}{trailing}</div>;
    $[4] = children;
    $[5] = leading;
    $[6] = ref;
    $[7] = t4;
    $[8] = trailing;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
});
interface ChatAttachInputProps {
  /** Stable id so file-picker automation can target the input directly. */
  inputId: string;
  accept?: string;
  multiple?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** Renders the visible trigger; call `open` to show the file picker. */
  children: (open: () => void) => React.ReactNode;
}

/** Hidden file input plus a caller-supplied trigger. */
export function ChatAttachInput({
  inputId,
  accept,
  multiple = true,
  onChange,
  inputRef,
  children
}: ChatAttachInputProps) {
  const fallbackRef = useRef<HTMLInputElement>(null);
  const ref = inputRef ?? fallbackRef;
  return <>
      <input ref={ref} id={inputId} type="file" accept={accept} multiple={multiple} onChange={onChange} className="hidden" />
      {children(() => ref.current?.click())}
    </>;
}